/**
 * Right-rail notes: compact cards that open a centered modal with the same
 * insight drill-down used in the domain feed (metrics, entity rows, effects).
 */
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { ChevronRight, X } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useFeed } from "@/context/FeedContext";
import { FeedEventDetail } from "@/features/feed/FeedEventDetail";
import { FeedInsightDetail, hasInsightDetail } from "@/features/feed/FeedInsightDetail";
import { FormattedFeedText } from "@/features/feed/FormattedFeedText";
import { CtaLink, InsightFlush } from "@/features/feed/insights/primitives";
import { formatRelativeTime } from "@/features/feed/feedVisuals";
import type { FeedEvent } from "@/types/feed";
import type { WorkspaceNote } from "@/types/workspace";
import { noteCtaLabel } from "./noteDetail";

function NoteCard({
  note,
  onOpen,
  layout = "card",
}: {
  note: WorkspaceNote;
  onOpen: () => void;
  layout?: "card" | "row";
}) {
  const isRow = layout === "row";
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-haspopup="dialog"
      className={
        isRow
          ? "flex min-h-11 w-full items-start gap-2 py-2.5 text-left transition-colors hover:bg-accent/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          : "block w-full rounded-lg border border-border/60 bg-foreground/[0.03] p-3 text-left transition-colors hover:bg-foreground/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      }
    >
      <div className={isRow ? "min-w-0 flex-1" : undefined}>
        <div className="mb-1 flex items-center justify-between gap-2">
          <p className="text-sm font-medium leading-tight">
            <FormattedFeedText text={note.title} scope={note.privacyScope} />
          </p>
          <span className="flex shrink-0 items-center gap-1">
            <Badge
              variant={note.tag === "Alert" ? "urgent" : "info"}
              className="px-1.5 py-0 text-[10px]"
            >
              {note.tag}
            </Badge>
            <ChevronRight className="size-3.5 text-muted-foreground" aria-hidden />
          </span>
        </div>
        {note.body && (
          <p className="text-xs text-muted-foreground">
            <FormattedFeedText text={note.body} scope={note.privacyScope} />
          </p>
        )}
      </div>
    </button>
  );
}

function NoteDetailDialog({
  note,
  event,
  onOpenChange,
}: {
  note: WorkspaceNote;
  event: FeedEvent | undefined;
  onOpenChange: (open: boolean) => void;
}) {
  const location = useLocation();
  const openedAt = useRef(location.key);
  const insightDetail = event ? hasInsightDetail(event.payload) : false;
  const hasEffects = (event?.children_count ?? 0) > 0;
  const href = note.href;
  const cta = href ? noteCtaLabel(href, event) : null;

  useEffect(() => {
    if (location.key !== openedAt.current) onOpenChange(false);
  }, [location.key, onOpenChange]);

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent
        className="glass-overlay top-1/2 flex max-h-[min(82dvh,44rem)] w-[min(42rem,calc(100vw-1.5rem))] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl border border-border/50 p-0 shadow-2xl data-[state=closed]:slide-out-to-top-0 data-[state=open]:slide-in-from-top-0"
        aria-describedby={undefined}
      >
        <div className="flex items-start gap-3 border-b border-border/50 px-5 py-4">
          <div className="min-w-0 flex-1 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <DialogTitle className="text-base font-semibold leading-tight">
                <FormattedFeedText text={note.title} scope={note.privacyScope} />
              </DialogTitle>
              <Badge
                variant={note.tag === "Alert" ? "urgent" : "info"}
                className="px-1.5 py-0 text-[10px]"
              >
                {note.tag}
              </Badge>
            </div>
            {note.body ? (
              <DialogDescription className="text-sm leading-snug">
                <FormattedFeedText text={note.body} scope={note.privacyScope} />
              </DialogDescription>
            ) : (
              <DialogDescription className="sr-only">
                Insight detail
              </DialogDescription>
            )}
            {event && (
              <p className="text-[11px] text-muted-foreground">
                {formatRelativeTime(event.created_at)}
              </p>
            )}
          </div>
          <DialogClose
            className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-foreground/10 hover:text-foreground"
            aria-label="Close"
          >
            <X className="size-4" />
          </DialogClose>
        </div>

        <ScrollArea className="min-h-0 flex-1 overflow-y-auto">
          <div className="space-y-3 px-5 py-4">
            {insightDetail && event && (
              <InsightFlush>
                <FeedInsightDetail payload={event.payload} eventId={event.id} />
              </InsightFlush>
            )}
            {hasEffects && event && <FeedEventDetail eventId={event.id} />}
            {!insightDetail && !hasEffects && !href && (
              <p className="text-xs text-muted-foreground">
                No further breakdown for this note.
              </p>
            )}
            {cta && href && <CtaLink to={href}>{cta}</CtaLink>}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

export function NotesList({
  notes,
  loading,
  layout = "card",
}: {
  notes: WorkspaceNote[];
  loading: boolean;
  /** Card tiles in the desktop rail; flush rows in the mobile sheet. */
  layout?: "card" | "row";
}) {
  const { events } = useFeed();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = notes.find((n) => n.id === selectedId) ?? null;
  const selectedEvent = events.find((e) => e.id === selectedId);

  if (loading && notes.length === 0) {
    return <p className="pt-1 text-xs text-muted-foreground">Loading notes…</p>;
  }
  if (notes.length === 0) {
    return <p className="pt-1 text-xs text-muted-foreground">No notes right now.</p>;
  }

  return (
    <>
      <div
        className={
          layout === "row"
            ? "divide-y divide-border/40 pt-0.5"
            : "space-y-2 pt-1"
        }
      >
        {notes.map((note) => (
          <NoteCard
            key={note.id}
            note={note}
            layout={layout}
            onOpen={() => setSelectedId(note.id)}
          />
        ))}
      </div>
      {selected && (
        <NoteDetailDialog
          note={selected}
          event={selectedEvent}
          onOpenChange={(open) => {
            if (!open) setSelectedId(null);
          }}
        />
      )}
    </>
  );
}
